import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ReportShell from '../../../shared/ReportShell';
import { AvailabilityStackedBadge } from '../../../shared/Pill';
import { previewFileUrl } from '../../../shared/mediaFileUrl';
import ParagraphText from '../../../shared/ParagraphText';
import PricelistAboutUs from './PricelistAboutUs';
import PricelistContactBlock from './PricelistContactBlock';
import { formatDate, formatDateWords } from '../../../shared/tenantClock';
import { formatMoney, formatNumber } from '../../../shared/numberFormat';

// Print/PDF "extended card" version of a Pricelist — one flat, UoM-agnostic
// list of quantity breaks per product (extended Price for that row's
// own UoM — e.g. the price for one whole Box, not a per-Piece figure — next
// to the equivalent Base UoM quantity threshold, same as a printed supplier
// catalog page). Each row's image slot shows the Product's primary
// Marketing photo, or the generic placeholder when none is set — Technical
// drawings are never shown here, since they read fine full-size but are
// generally illegible shrunk into this small a slot (vis_product_media_library.md
// — this is the one place an image is actually consumed today). Same
// ReportShell (Print/Save-as-PDF, no PDF library) as PricelistCompactReport.js —
// just a different visual treatment of the same resolved rows.
function formatPrice(price) {
  return formatMoney(price) || '—';
}

// Date only, no time — unlike formatReportTimestamp() (ReportShell.js),
// which every other report uses. A Pricelist is browsed like a catalog,
// not audited down to the minute it was pulled, so the time only added
// noise here.
function formatReportDate(date = new Date()) {
  return `Generated ${formatDateWords(date, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}`;
}

function groupByProduct(rows) {
  const groups = [];
  rows.forEach((row) => {
    const lastGroup = groups[groups.length - 1];
    if (lastGroup && lastGroup.product_id === row.product_id) {
      lastGroup.rows.push(row);
    } else {
      groups.push({
        product_id: row.product_id,
        sku: row.sku,
        name: row.name,
        description: row.description,
        availability_state: row.availability_state,
        availability_badge: row.availability_badge,
        availability_badge_color: row.availability_badge_color,
        availability_date: row.availability_date,
        base_rrp: row.base_rrp,
        base_uom_code: row.base_uom_code,
        rows: [row],
      });
    }
  });
  return groups;
}

// UoM is its own column here (unlike the flat list this used to show) —
// sub-groups a product's rows by uom_id so each UoM's name only prints
// once, rowSpan'd across its own quantity-break tiers, same convention
// PricelistCompactReport.js's card table already uses.
function groupByUom(rows) {
  const groups = [];
  rows.forEach((row) => {
    const lastGroup = groups[groups.length - 1];
    if (lastGroup && lastGroup.uom_id === row.uom_id) {
      lastGroup.rows.push(row);
    } else {
      groups.push({ uom_id: row.uom_id, uom_name: row.uom_name, rows: [row] });
    }
  });
  return groups;
}

function groupByCategory(rows) {
  const sections = [];
  rows.forEach((row) => {
    const lastSection = sections[sections.length - 1];
    if (lastSection && lastSection.category_id === row.product_category_id) {
      lastSection.rows.push(row);
    } else {
      sections.push({
        category_id: row.product_category_id,
        category_name: row.product_category_name,
        category_description: row.product_category_description,
        rows: [row],
      });
    }
  });
  return sections;
}

// Generic "no image" placeholder — self-contained SVG, no external asset —
// stands in for real product photography/technical drawings, which this
// system has no field for yet.
function PlaceholderImage() {
  return (
    <svg width="72" height="72" viewBox="0 0 72 72" style={{ display: 'block', margin: '0 auto' }}>
      <rect x="1" y="1" width="70" height="70" rx="6" fill="#f6f8fa" stroke="#cdd5df" strokeWidth="1.5" />
      <circle cx="26" cy="26" r="6" fill="#cdd5df" />
      <path d="M8 54 L28 34 L40 46 L50 36 L64 54 Z" fill="#cdd5df" />
    </svg>
  );
}

const CELL_STYLE = { padding: '10px 8px', borderBottom: '1px solid #e6e6e6', verticalAlign: 'top' };
// Tighter vertical padding for the two cells that repeat once per quantity-
// break tier (Quantity Break, Price) — CELL_STYLE's 10px is sized for
// the once-per-product SKU/Description/Image cells it's rowSpan'd across,
// which reads as too much air stacked between a single product's own tiers.
const TIER_CELL_STYLE = { ...CELL_STYLE, padding: '1px 8px' };

// Same 72x72 box either way. Uses the `preview` derivative rather than
// `thumb` — this is a print/PDF document (see file header), and a 72px CSS
// box still needs real pixels behind it to stay crisp on paper, not just
// "small enough for a screen list row". `src` is already a resolved URL
// (an /api/media-images/:id/preview URL on screen, or a data: URI when
// server-rendered for the Compose Email PDF attachment — issue #238 —
// which has no authenticated session to fetch that route through).
function ProductImage({ src }) {
  if (!src) return <PlaceholderImage />;
  return (
    <img
      src={src}
      alt=""
      width={72}
      height={72}
      style={{ display: 'block', margin: '0 auto', objectFit: 'contain', borderRadius: 6, border: '1.5px solid #cdd5df', background: '#f6f8fa' }}
    />
  );
}

// `companyInfo`/`primaryImages` — see useCompanyName.js and ProductImage
// above — both threaded through for the server-rendered PDF attachment
// path (issue #238). When `primaryImages` is supplied (a plain
// {productId: url} map) the normal fetch is skipped — moot server-side
// anyway since effects never run during ReactDOMServer.renderToStaticMarkup,
// this guard just avoids a wasted call if reused with it in a live browser
// context later.
function PricelistExtendedReport({ pricelist, result, defaultTaxRate, showRrp, onClose, companyInfo, primaryImages: suppliedPrimaryImages }) {
  const rows = result?.rows || [];
  const isEmpty = rows.length === 0;
  let rowNumber = 0;

  const [primaryImages, setPrimaryImages] = useState(suppliedPrimaryImages || {});
  const productIds = [...new Set(rows.map((r) => r.product_id))];
  const productIdsKey = productIds.join(',');

  useEffect(() => {
    if (suppliedPrimaryImages) return;
    if (!productIdsKey) {
      setPrimaryImages({});
      return;
    }
    axios.get('/api/media-images/primary-for-products', { params: { product_ids: productIdsKey } })
      .then((res) => {
        const byProduct = res.data.data || {};
        const urls = {};
        Object.keys(byProduct).forEach((productId) => { urls[productId] = previewFileUrl(byProduct[productId].media_image_id); });
        setPrimaryImages(urls);
      })
      .catch(() => setPrimaryImages({}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productIdsKey]);

  return (
    <ReportShell
      title={`${pricelist.name} Pricelist`}
      subtitle={(
        <>
          {/* Screen-only here — printed separately, via footerNote below,
              in the same running-footer box as "Commercial in Confidence",
              so it doesn't compete with the title/category for room at the
              top of a printed page. */}
          <div className="no-print">{formatReportDate()}</div>
          <div>
            {pricelist.customer_price_group?.name} · {pricelist.product_price_group?.name || 'All products'}
            {pricelist.uom ? ` · ${pricelist.uom.code}` : ''} · Valid {formatDate(result?.validFrom)}
            {result?.validTo !== result?.validFrom ? ` to ${formatDate(result?.validTo)}` : ''}
          </div>
        </>
      )}
      footerNote={`Extended Pricelist · ${formatReportDate()}`}
      onClose={onClose}
      printDisabled={isEmpty}
      sidePadding={40}
      companyInfo={companyInfo}
    >
      {isEmpty ? (
        <div style={{ padding: 24, textAlign: 'center', color: '#888' }}>No sellable Product/UoM combinations match this scope.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
          <PricelistAboutUs companyInfo={companyInfo} />
          {groupByCategory(rows).map((section, sIdx) => (
            <div
              key={section.category_id ?? 'uncategorised'}
              // padding-top only takes effect here because this div starts
              // a genuinely fresh page (via the break below) — unlike
              // .print-area's own padding-top, which only applies once at
              // the start of a box that spans many pages by overflowing.
              // The category heading sits outside the <table> below, so the
              // <thead> spacer row (which only helps content repeating
              // inside that table) never reached it — on a Pricelist with
              // more than one Category, every heading after the first
              // collided with the running header. Verified empirically
              // (headless Chromium + pdfjs) before landing here; this repo
              // was only ever exercised with single-category test data
              // until now, which is how this stayed latent.
              style={sIdx > 0 ? { breakBefore: 'page', pageBreakBefore: 'always', paddingTop: 26 } : undefined}
            >
              <h3 style={{ fontSize: 14, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: '#3a6ea5', margin: '0 0 6px' }}>
                {section.category_name}
              </h3>
              {section.category_description && (
                <ParagraphText text={section.category_description} style={{ fontSize: 13, color: '#666', margin: '0 0 10px' }} />
              )}
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: '4%' }} />
                  <col style={{ width: '20%' }} />
                  {/* Description takes whatever the fixed columns leave. Price is
                      a fixed 150px (room for "Inc GST: AU$ 56.65") rather than a %, so
                      on a wide screen it doesn't grow and leave a gap after Quantity Break. */}
                  <col />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: 150 }} />
                </colgroup>
                <thead>
                  {/* Print-only, invisible on screen. <thead> repeats on every
                      printed page a table spans (browser default) — this
                      report's ReportShell running header (App.css's
                      .print-running-header) also repeats on every page, fixed
                      to the physical top, so without its own reserved
                      clearance the repeated column-header row below lands
                      right under/against it on continuation pages. A blank
                      spacer row repeats along with the real header row, using
                      the same reliable per-page repeat rather than trying to
                      offset the fixed header itself (empirically unstable in
                      Chromium's print engine — see the App.css comment). */}
                  <tr className="print-only-row"><td colSpan={6} style={{ height: 18, padding: 0, border: 'none' }} /></tr>
                  <tr style={{ borderTop: '1px solid #999', borderBottom: '1px solid #999' }}>
                    <th style={{ ...CELL_STYLE, textAlign: 'right', paddingLeft: 20, color: '#3a6ea5', textTransform: 'uppercase', fontSize: 12 }}>#</th>
                    <th style={{ ...CELL_STYLE, textAlign: 'center', color: '#3a6ea5', textTransform: 'uppercase', fontSize: 12 }}>SKU</th>
                    <th style={{ ...CELL_STYLE, textAlign: 'left', color: '#3a6ea5', textTransform: 'uppercase', fontSize: 12 }}>Description</th>
                    <th style={{ ...CELL_STYLE, textAlign: 'center', color: '#3a6ea5', textTransform: 'uppercase', fontSize: 12 }}>UoM</th>
                    <th style={{ ...CELL_STYLE, textAlign: 'center', color: '#3a6ea5', textTransform: 'uppercase', fontSize: 12 }}>Quantity<br />Break</th>
                    <th style={{ ...CELL_STYLE, textAlign: 'right', color: '#3a6ea5', textTransform: 'uppercase', fontSize: 12 }}>{pricelist.customer_price_group?.name ? `${pricelist.customer_price_group.name} Price` : 'Price'}<br />Ex GST</th>
                  </tr>
                </thead>
                {/* Print-only, invisible on screen. <tfoot> repeats at the
                    bottom of every printed page a table spans (same native
                    per-page repeat as <thead>), so a blank spacer row here
                    reserves clearance for the fixed running footer + the
                    "Generated..." line above it — without it, tbody rows
                    pack right up to the physical page edge on every page,
                    landing under/behind those two, which read as the last
                    row's own border and the footer's border merging into a
                    doubled line right through the "Generated..." text. */}
                <tfoot>
                  <tr className="print-only-row"><td colSpan={6} style={{ height: 40, padding: 0, border: 'none' }} /></tr>
                </tfoot>
                <tbody>
                  {groupByProduct(section.rows).map((group) => {
                    rowNumber += 1;
                    const tierCount = group.rows.length;
                    const uomGroups = groupByUom(group.rows);
                    let productRowIdx = -1;
                    return uomGroups.map((uomGroup) => (
                      uomGroup.rows.map((row, uomTierIdx) => {
                        productRowIdx += 1;
                        return (
                          <tr key={`${row.uom_id}-${row.min_quantity}-${productRowIdx}`} style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
                            {productRowIdx === 0 && (
                              <>
                                <td style={{ ...CELL_STYLE, textAlign: 'right', paddingLeft: 20, color: '#888' }} rowSpan={tierCount}>{rowNumber}</td>
                                <td style={{ ...CELL_STYLE, textAlign: 'center', paddingLeft: 14 }} rowSpan={tierCount}>
                                  {/* Long SKUs wrap at any character rather than truncating
                                      with an ellipsis, which hid the part of the SKU that
                                      tells similar products apart. The extra left padding
                                      keeps a full-width SKU off the right-aligned # beside it. */}
                                  <div style={{ fontWeight: 600, overflowWrap: 'anywhere', marginBottom: 4 }}>{group.sku}</div>
                                  {/* One RRP per Product, per Base UoM unit (Base UoM
                                      Retail Price as of Valid From, inc GST) — under
                                      the SKU so it adds no table height. Blank when
                                      none is in force. */}
                                  {showRrp && defaultTaxRate !== null && group.base_rrp !== null && group.base_rrp !== undefined && (
                                    <div style={{ fontSize: 11, color: '#3a6ea5', fontWeight: 600, marginBottom: 4 }}>
                                      RRP inc GST
                                      <div style={{ whiteSpace: 'nowrap' }}>AU$ {formatPrice(group.base_rrp * (1 + defaultTaxRate)).replace('$', '')}/{group.base_uom_code}</div>
                                    </div>
                                  )}
                                  <div style={{ width: 72, height: 72, margin: '0 auto' }}>
                                    <ProductImage src={primaryImages[String(group.product_id)]} />
                                  </div>
                                  {/* Under the photo, not overlaid: stacked pills covered a
                                      good part of the 72px image. This cell has height to
                                      spare because the Name/Description (soon Specs) cell
                                      beside it is what sets the row's height. */}
                                  <AvailabilityStackedBadge badge={group.availability_badge} date={group.availability_date} color={group.availability_badge_color} />
                                </td>
                                <td style={{ ...CELL_STYLE, whiteSpace: 'pre-line', color: '#444' }} rowSpan={tierCount}>
                                  {group.name}
                                  {group.description && (
                                    <ParagraphText text={group.description} style={{ marginTop: 4, fontSize: 12, color: '#888' }} />
                                  )}
                                </td>
                              </>
                            )}
                            {uomTierIdx === 0 && (
                              <td style={{ ...CELL_STYLE, textAlign: 'center', color: '#888' }} rowSpan={uomGroup.rows.length}>{uomGroup.uom_name}</td>
                            )}
                            <td style={{ ...TIER_CELL_STYLE, textAlign: 'center' }}>
                              {row.ambiguous || row.needs_price ? '—' : (
                                <>
                                  <div>{formatNumber(row.min_quantity, { maxDecimals: 4 })} {row.uom_code}</div>
                                  {row.base_quantity !== row.min_quantity && (
                                    <div style={{ fontSize: 12, color: '#888', fontWeight: 400 }}>
                                      {formatNumber(row.base_quantity, { maxDecimals: 4 })} {row.base_uom_code}
                                    </div>
                                  )}
                                </>
                              )}
                            </td>
                            <td style={{ ...TIER_CELL_STYLE, textAlign: 'right', color: '#2e7d32' }}>
                              {row.ambiguous ? 'Ambiguous' : row.needs_price ? 'Needs price' : (
                                <>
                                  <div style={{ whiteSpace: 'nowrap' }}>AU$ {formatPrice(row.price).replace('$', '')}/{row.uom_code}</div>
                                  {row.price !== row.equiv_price && (
                                    <div style={{ fontSize: 12, color: '#888', fontWeight: 400, whiteSpace: 'nowrap' }}>
                                      {formatPrice(row.equiv_price)}/{row.base_uom_code}
                                    </div>
                                  )}
                                  {defaultTaxRate !== null && (
                                    <div style={{ fontSize: 12, color: '#888', fontWeight: 400, whiteSpace: 'nowrap' }}>
                                      Inc GST: AU$ {formatPrice(row.price * (1 + defaultTaxRate)).replace('$', '')}
                                    </div>
                                  )}
                                </>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    ));
                  })}
                </tbody>
              </table>
            </div>
          ))}
          <PricelistContactBlock companyInfo={companyInfo} />
        </div>
      )}
    </ReportShell>
  );
}

export default PricelistExtendedReport;
